import 'aws-amplify/auth/enable-oauth-listener';
import { useEffect, useState, useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { fetchAuthSession, getCurrentUser, fetchUserAttributes, signOut } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import {
    signInSuccess,
    signOutSuccess,
    setLoading,
    setVerificationStatus,
} from '../store/slices/authSlice';
import {
    setUser,
    clearUser,
    setUserVerificationStatus,
} from '../store/slices/userSlice';
import type { BackendUser } from '../types/auth.types';

const useAuthSession = () => {
    const dispatch = useAppDispatch();
    const [isInitializing, setIsInitializing] = useState(true);
    const { signedIn } = useAppSelector((state) => state.auth);

    const isAuthError = useCallback((error: unknown): boolean => {
        const err = error as { message?: string; name?: string; toString?: () => string };
        const errorMessage = err?.message || err?.toString?.() || '';
        const errorName = err?.name || '';

        const authErrorIndicators = [
            'NotAuthorizedException',
            'UnauthorizedException',
            'UserNotFoundException',
            'UserUnAuthenticatedException',
            'TokenRefreshException',
            'NoCredentials',
            'NoSession',
            'needs to be authenticated',
            'User needs to be authenticated',
        ];

        return authErrorIndicators.some(indicator =>
            errorMessage.includes(indicator) || errorName.includes(indicator)
        );
    }, []);

    const decodeJWT = useCallback((token: string) => {
        try {
            const base64Url = token.split('.')[1];
            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
            const jsonPayload = decodeURIComponent(
                atob(base64)
                    .split('')
                    .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                    .join('')
            );
            return JSON.parse(jsonPayload);
        } catch {
            return null;
        }
    }, []);

    const getUserAttributes = useCallback(async (idToken?: string | null) => {
        if (idToken) {
            try {
                const decodedToken = decodeJWT(idToken);
                if (decodedToken) {
                    return {
                        email: decodedToken.email || decodedToken['cognito:username'] || '',
                        given_name: decodedToken.given_name || decodedToken.name?.split(' ')[0] || '',
                        family_name: decodedToken.family_name || decodedToken.name?.split(' ').slice(1).join(' ') || '',
                        email_verified: decodedToken.email_verified || true,
                        identities: decodedToken.identities,
                        phone_number: decodedToken.phone_number,
                        birthdate: decodedToken.birthdate,
                        gender: decodedToken.gender,
                        'custom:user_type': decodedToken['custom:user_type'],
                        picture: decodedToken.picture,
                    };
                }
            } catch {
                // Ignore decode errors
            }
        }

        try {
            return await fetchUserAttributes();
        } catch (attrError: unknown) {
            const error = attrError as { message?: string };
            if (error?.message?.includes('scopes') || error?.message?.includes('scope')) {
                return {
                    email: '',
                    given_name: '',
                    family_name: '',
                    email_verified: true,
                    identities: null,
                };
            }
            throw attrError;
        }
    }, [decodeJWT]);

    const setupUserSession = useCallback(async (): Promise<void> => {
        dispatch(setLoading(true));
        try {
            const session = await fetchAuthSession({ forceRefresh: false });

            if (session.tokens?.accessToken) {
                const idToken = session.tokens.idToken?.toString() || null;
                dispatch(signInSuccess({
                    token: session.tokens.accessToken.toString(),
                    idToken: idToken,
                }));

                const user = await getCurrentUser();
                const attributes = await getUserAttributes(idToken);

                if (!attributes) {
                    return;
                }

                const isVerified = attributes.email_verified === 'true' ||
                    (typeof attributes.email_verified === 'boolean' ? attributes.email_verified : false) ||
                    true;

                dispatch(setVerificationStatus(isVerified));
                dispatch(setUserVerificationStatus(isVerified));

                const userData: BackendUser = {
                    id: user.userId,
                    first_name: attributes.given_name || attributes.email?.split('@')[0] || '',
                    last_name: attributes.family_name || '',
                    email: attributes.email || '',
                    user_type: attributes['custom:user_type'] || 'USER',
                    is_email_verified: isVerified,
                    auth_provider: attributes.identities ? 'google' : 'cognito',
                    ...(attributes.phone_number && { phone_number: attributes.phone_number }),
                    ...(attributes.birthdate && { dob: attributes.birthdate }),
                    ...(attributes.gender && { gender: attributes.gender }),
                    ...(attributes.picture && { avatar: attributes.picture }),
                };

                dispatch(setUser(userData));
            }
        } catch (error) {
            if (isAuthError(error)) {
                try {
                    await signOut();
                    dispatch(signOutSuccess());
                    dispatch(clearUser());
                } catch {
                    // Ignore signOut errors
                }
            }
        } finally {
            dispatch(setLoading(false));
        }
    }, [dispatch, isAuthError, getUserAttributes]);

    useEffect(() => {
        const hubListenerCancel = Hub.listen('auth', async ({ payload }) => {
            switch (payload.event) {
                case 'signInWithRedirect':
                    try {
                        await setupUserSession();
                    } catch {
                        // Ignore OAuth callback errors
                    } finally {
                        setIsInitializing(false);
                    }
                    break;

                case 'signInWithRedirect_failure':
                    setIsInitializing(false);
                    break;

                case 'signedOut':
                    dispatch(signOutSuccess());
                    dispatch(clearUser());
                    break;

                default:
                    break;
            }
        });

        const initializeAuth = async () => {
            if (!signedIn) {
                setIsInitializing(false);
                return;
            }

            const sessionCheck = fetchAuthSession({ forceRefresh: false }).catch(() => null);
            const timeout = new Promise(resolve => setTimeout(() => resolve(null), 2000));

            try {
                const session = await Promise.race([sessionCheck, timeout]) as Awaited<ReturnType<typeof fetchAuthSession>> | null;
                const hasValidSession = session?.tokens?.accessToken;

                if (hasValidSession) {
                    try {
                        await getCurrentUser();
                        await setupUserSession();
                    } catch (userError) {
                        if (isAuthError(userError)) {
                            try {
                                await signOut();
                            } catch {
                                return;
                            }
                            dispatch(signOutSuccess());
                            dispatch(clearUser());
                        }
                    }
                } else {
                    dispatch(signOutSuccess());
                    dispatch(clearUser());
                }
            } catch (error) {
                if (isAuthError(error) || signedIn) {
                    dispatch(signOutSuccess());
                    dispatch(clearUser());
                }
            } finally {
                setIsInitializing(false);
            }
        };

        initializeAuth();

        return () => {
            hubListenerCancel();
        };
    }, [dispatch, setupUserSession, signedIn, isAuthError]);

    return { isInitializing };
};

export default useAuthSession;
